<div
  class="ai-dashboard"
  [class.ai-dashboard--revealed]="contentRevealed"
  [class.ai-dashboard--closing]="closing"
  [class.ai-dashboard--loading-exit]="loadingExiting">

  <!-- Ambient particles -->
  <div class="ai-dashboard__particles" aria-hidden="true">
    <span
      *ngFor="let p of particleSeeds"
      class="ai-particle"
      [style.--i]="p"></span>
  </div>
  <div class="ai-dashboard__border-glow" aria-hidden="true"></div>

  <!-- Header -->
  <header class="ai-dashboard__header ai-reveal" style="--reveal-order: 0">
    <div class="ai-dashboard__header-left">
      <div class="ai-dashboard__icon" aria-hidden="true">
        <span class="ai-dashboard__icon-orbit"></span>
        <mat-icon class="ai-dashboard__icon-spark">auto_awesome</mat-icon>
      </div>
      <div>
        <h1 class="ai-dashboard__title">AI Insights</h1>
        <p class="ai-dashboard__subtitle">
          <mat-icon>auto_awesome</mat-icon>
          Smart analysis generated by AI
        </p>
      </div>
    </div>

    <div class="ai-dashboard__header-right">
      <div class="ai-dashboard__vessel-block">
        <span class="ai-dashboard__vessel-name">{{ vesselDisplayName }}</span>
        <span class="ai-dashboard__vessel-imo">{{ vesselImo }}</span>
      </div>
      <div class="ai-dashboard__badges">
        <span
          class="ai-badge ai-badge--muted"
          [class.ai-badge--pulse]="contentRevealed">
          <mat-icon>schedule</mat-icon>
          {{ generatedLabel }}
        </span>
        <span
          class="ai-badge ai-badge--success"
          [class.ai-badge--pulse]="contentRevealed"
          [attr.title]="confidenceHint">
          <mat-icon>verified</mat-icon>
          AI Confidence <strong>{{ displayedConfidence }}%</strong>
        </span>
      </div>
      <button type="button" class="ai-dashboard__close-x" (click)="close()" aria-label="Close">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  </header>

  <!-- AI loading experience -->
  <section *ngIf="loading" class="ai-loader" aria-live="polite">
    <div class="ai-loader__waves" aria-hidden="true">
      <span
        *ngFor="let w of loadingWaveSeeds"
        class="ai-loader__wave"
        [class.ai-loader__wave--right]="w % 2 === 1"
        [style.--w]="w"></span>
    </div>

    <div class="ai-loader__center">
      <div class="ai-loader__orb">
        <span class="ai-loader__ring ai-loader__ring--outer"></span>
        <span class="ai-loader__ring ai-loader__ring--inner"></span>
        <div class="ai-loader__core">
          <mat-icon>psychology</mat-icon>
        </div>
      </div>
      <h2 class="ai-loader__title">Generating AI insights</h2>
      <p class="ai-loader__subtitle">
        {{ loadingSubMessage }}<span class="ai-loader__dots" aria-hidden="true"><span></span><span></span><span></span></span>
      </p>
    </div>

    <ol class="ai-loader__steps">
      <li
        *ngFor="let step of loadingSteps; let i = index"
        class="ai-loader__step"
        [class.ai-loader__step--active]="step.state === 'active'"
        [class.ai-loader__step--completed]="step.state === 'completed'"
        [class.ai-loader__step--pending]="step.state === 'pending'">
        <span class="ai-loader__step-node" aria-hidden="true"></span>
        <mat-icon class="ai-loader__step-icon">{{ step.icon }}</mat-icon>
        <span class="ai-loader__step-label">{{ step.label }}</span>
        <span *ngIf="step.state === 'active'" class="ai-loader__step-badge">
          {{ activeLoadingStepNumber }} / {{ loadingSteps.length }}
        </span>
      </li>
    </ol>

    <footer class="ai-loader__notice">
      <mat-icon>info</mat-icon>
      <span>
        Please wait while we analyze the complete report.
        <em>This may take a few moments.</em>
      </span>
    </footer>
  </section>

  <!-- Error -->
  <div *ngIf="!loading && errorMessage" class="ai-dashboard__state ai-dashboard__state--error ai-reveal">
    <mat-icon>error_outline</mat-icon>
    <p>{{ errorMessage }}</p>
    <button type="button" class="btn btn--ghost" (click)="close()">Close</button>
  </div>

  <!-- Body -->
  <div *ngIf="!loading && !errorMessage" class="ai-dashboard__body">

    <div class="ai-dashboard__scroll">
    <div class="ai-dashboard__row ai-dashboard__row--top">
      <article class="ai-card ai-card--summary ai-reveal" style="--reveal-order: 1">
        <span class="ai-card__shine" aria-hidden="true"></span>
        <header class="ai-card__head">
          <mat-icon>description</mat-icon>
          <span>Executive summary</span>
        </header>
        <p class="ai-card__summary-text">{{ executiveSummary }}</p>
        <div class="kpi-grid">
          <div class="kpi-tile" *ngFor="let k of kpis; let i = index" [style.--reveal-order]="i">
            <mat-icon>{{ k.icon }}</mat-icon>
            <div>
              <span class="kpi-tile__value">{{ k.value }}</span>
              <span class="kpi-tile__label">{{ k.label }}</span>
            </div>
          </div>
        </div>
        <div class="chip-row">
          <span
            *ngFor="let chip of insightTags; let i = index"
            class="status-chip"
            [class.status-chip--ai-pulse]="contentRevealed"
            [style.animation-delay]="(i * 120) + 'ms'"
            [class.status-chip--orange]="chip.variant === 'orange'"
            [class.status-chip--blue]="chip.variant === 'blue'"
            [class.status-chip--purple]="chip.variant === 'purple'"
            [class.status-chip--yellow]="chip.variant === 'yellow'">
            {{ chip.label }}
          </span>
        </div>
      </article>

      <article class="ai-card ai-card--score ai-reveal" style="--reveal-order: 2">
        <span class="ai-card__shine" aria-hidden="true"></span>
        <header class="ai-card__head ai-card__head--center">
          <span>AI operational score</span>
        </header>
        <div class="score-gauge">
          <div
            class="score-gauge__ring"
            [class.score-gauge__ring--animate]="contentRevealed"
            [class.score-gauge__ring--complete]="scoreRingComplete"
            [ngStyle]="scoreRingStyle">
            <div class="score-gauge__inner">
              <span class="score-gauge__value">{{ displayedScore }}%</span>
              <span class="score-gauge__label" [ngClass]="scoreLabelClass">
                {{ scoreLabel }}
              </span>
            </div>
          </div>
        </div>
        <p class="score-gauge__caption">Overall performance</p>
        <button
          type="button"
          class="score-gauge__explain"
          (click)="toggleScoreExplain()">
          Why this score?
          <mat-icon>{{ showScoreExplain ? 'expand_less' : 'expand_more' }}</mat-icon>
        </button>
        <div class="score-explain" *ngIf="showScoreExplain">
          <ul class="score-explain__reasons" *ngIf="scoreReasons.length">
            <li *ngFor="let reason of scoreReasons">{{ reason }}</li>
          </ul>
          <ul class="score-explain__list">
            <li *ngFor="let item of scoreBreakdown" class="score-explain__item">
              <span>{{ item.label }}</span>
              <strong [class.score-explain__pos]="item.impact > 0" [class.score-explain__neg]="item.impact < 0">
                {{ item.impact > 0 ? '+' : '' }}{{ item.impact }}
              </strong>
            </li>
          </ul>
          <p class="score-explain__final">
            Final Operational Score: <strong>{{ operationalScore }}%</strong>
            ({{ scoreLabel }})
          </p>
        </div>
      </article>

      <article class="ai-card ai-card--risks ai-reveal" style="--reveal-order: 3">
        <span class="ai-card__shine" aria-hidden="true"></span>
        <header class="ai-card__head">
          <mat-icon>shield</mat-icon>
          <span>Risk indicators</span>
        </header>
        <ul class="risk-list">
          <li *ngFor="let r of riskIndicators; let i = index" class="risk-item" [style.--reveal-order]="i">
            <div class="risk-item__top">
              <span class="risk-item__label">{{ r.label }}</span>
              <span
                class="risk-item__badge"
                [class.risk-item__badge--low]="r.level === 'low'"
                [class.risk-item__badge--medium]="r.level === 'medium'"
                [class.risk-item__badge--high]="r.level === 'high'">
                {{ r.level === 'low' ? 'Low' : r.level === 'medium' ? 'Medium' : 'High' }}
              </span>
            </div>
            <div class="risk-item__bar">
              <span
                *ngFor="let seg of [1,2,3,4]; let i = index"
                class="risk-seg"
                [class.risk-seg--on]="i < getRiskSegmentsOn(r)"
                [class.risk-seg--medium]="r.level === 'medium'"
                [class.risk-seg--high]="r.level === 'high'">
              </span>
            </div>
            <span class="risk-item__pct">{{ r.animatedPercent ?? 0 }}%</span>
          </li>
        </ul>
        <button type="button" class="ai-link-btn">View risk details <mat-icon>chevron_right</mat-icon></button>
      </article>
    </div>

    <article
      class="ai-card ai-card--benchmark ai-reveal"
      style="--reveal-order: 3.5"
      *ngIf="benchmark">
      <span class="ai-card__shine" aria-hidden="true"></span>
      <header class="ai-card__head">
        <mat-icon>bar_chart</mat-icon>
        <span>Benchmark comparison</span>
      </header>
      <div class="benchmark-grid">
        <div class="benchmark-metrics">
          <div class="benchmark-metric">
            <span class="benchmark-metric__label">Actual Consumption</span>
            <span class="benchmark-metric__value">{{ formatMt(benchmark.actualMt, benchmark.tcpVariancePct) }}</span>
          </div>
          <div class="benchmark-metric">
            <span class="benchmark-metric__label">TCP Benchmark</span>
            <span class="benchmark-metric__value">
              {{ formatMt(benchmark.tcpMt, benchmark.tcpVariancePct) }}
              <em *ngIf="benchmark.tcpVariancePct != null || benchmark.tcpVarianceMt != null">
                ({{ formatBenchmarkVariance(benchmark.tcpVariancePct, benchmark.tcpVarianceMt) }})
              </em>
            </span>
          </div>
          <div class="benchmark-metric">
            <span class="benchmark-metric__label">Technical Baseline</span>
            <span class="benchmark-metric__value">
              {{ formatMt(benchmark.baselineMt, benchmark.baselineVariancePct) }}
              <em *ngIf="benchmark.baselineVariancePct != null || benchmark.baselineVarianceMt != null">
                ({{ formatBenchmarkVariance(benchmark.baselineVariancePct, benchmark.baselineVarianceMt) }})
              </em>
            </span>
          </div>
        </div>
        <div class="benchmark-chart" *ngIf="benchmarkChartMax > 0">
          <div class="benchmark-bar-row">
            <span class="benchmark-bar-row__label">Actual</span>
            <div class="benchmark-bar-row__track">
              <span
                class="benchmark-bar benchmark-bar--actual"
                [style.width.%]="getBenchmarkBarWidth(benchmark.actualMt)"></span>
            </div>
          </div>
          <div class="benchmark-bar-row">
            <span class="benchmark-bar-row__label">TCP</span>
            <div class="benchmark-bar-row__track">
              <span
                class="benchmark-bar benchmark-bar--tcp"
                [style.width.%]="getBenchmarkBarWidth(benchmark.tcpMt)"></span>
            </div>
          </div>
          <div class="benchmark-bar-row">
            <span class="benchmark-bar-row__label">Baseline</span>
            <div class="benchmark-bar-row__track">
              <span
                class="benchmark-bar benchmark-bar--baseline"
                [style.width.%]="getBenchmarkBarWidth(benchmark.baselineMt)"></span>
            </div>
          </div>
        </div>
        <p *ngIf="!benchmark.hasData" class="benchmark-empty">
          Benchmark MT values not found; variance percentages used where available.
        </p>
      </div>
    </article>

    <div class="ai-dashboard__row ai-dashboard__row--mid">
      <article class="ai-card ai-card--findings ai-reveal" style="--reveal-order: 4">
        <span class="ai-card__shine" aria-hidden="true"></span>
        <header class="ai-card__head">
          <mat-icon>lightbulb</mat-icon>
          <span>AI key findings</span>
        </header>
        <ul class="findings-list" *ngIf="typingFindings.length">
          <li
            *ngFor="let item of typingFindings; let i = index"
            class="findings-list__item"
            [class.findings-list__item--typing]="!item.complete">
            <span class="findings-list__dot"></span>
            <span *ngIf="!item.complete" class="findings-list__typed">
              {{ item.visible }}<span class="findings-list__cursor" aria-hidden="true"></span>
            </span>
            <span *ngIf="item.complete" [innerHTML]="formatInsightHtml(item.full)"></span>
          </li>
        </ul>
        <p *ngIf="!typingFindings.length && !aiInsights.length" class="ai-empty-inline">No key findings returned.</p>
        <div class="ai-card__decor" aria-hidden="true"></div>
      </article>

      <article class="ai-card ai-card--actions ai-reveal" style="--reveal-order: 5">
        <span class="ai-card__shine" aria-hidden="true"></span>
        <header class="ai-card__head">
          <mat-icon>track_changes</mat-icon>
          <span>Recommended actions</span>
          <mat-icon class="ai-card__decor-icon">auto_awesome</mat-icon>
        </header>
        <ul class="actions-list" *ngIf="guidance.length">
          <li
            *ngFor="let g of guidance; let i = index"
            class="actions-list__item ai-reveal"
            [style.--reveal-order]="i">
            <mat-icon class="actions-list__check">check_circle</mat-icon>
            <span [innerHTML]="formatInsightHtml(g)"></span>
          </li>
        </ul>
        <p *ngIf="!guidance.length" class="ai-empty-inline">No recommended actions returned.</p>
      </article>
    </div>
    </div>

    <section class="ai-dashboard__experts ai-reveal" style="--reveal-order: 6">
      <header class="ai-section-head">
        <mat-icon>groups</mat-icon>
        <span>Expert comments</span>
      </header>
      <div class="expert-row">
        <article
          *ngFor="let c of expertComments; let i = index"
          class="expert-card-v2 ai-reveal"
          [style.--reveal-order]="i"
          [style.--expert-accent]="c.accent">
          <button type="button" class="expert-card-v2__menu" aria-label="More">
            <mat-icon>more_horiz</mat-icon>
          </button>
          <div class="expert-card-v2__avatar">
            <span class="expert-card-v2__ai-glow" aria-hidden="true"></span>
            <mat-icon>{{ c.icon }}</mat-icon>
          </div>
          <h4 class="expert-card-v2__role">{{ c.role }}</h4>
          <p class="expert-card-v2__text">{{ c.comment }}</p>
          <button
            type="button"
            class="expert-card-v2__read"
            [ngClass]="c.linkClass"
            (click)="openExpertModal(c)">
            Read more
            <mat-icon>chevron_right</mat-icon>
          </button>
        </article>
      </div>
    </section>

  </div>

  <footer class="ai-dashboard__footer ai-reveal" style="--reveal-order: 7" *ngIf="!loading && !errorMessage">
    <p class="ai-dashboard__disclaimer">
      <mat-icon>info</mat-icon>
      AI insights are generated based on available data and may contain inaccuracies.
    </p>
    <div class="ai-dashboard__footer-actions">
      <button type="button" class="btn btn--outline" (click)="downloadAiAnalysis()">
        <mat-icon>picture_as_pdf</mat-icon>
        Download AI Analysis
      </button>
      <button type="button" class="btn btn--outline" (click)="downloadReport()">
        <mat-icon>download</mat-icon>
        Download report
      </button>
      <button type="button" class="btn btn--primary" (click)="close()">
        <mat-icon>close</mat-icon>
        Close
      </button>
    </div>
  </footer>

  <div
    class="expert-modal-overlay"
    *ngIf="expertModal"
    (click)="closeExpertModal()"
    role="dialog"
    aria-modal="true">
    <article class="expert-modal" (click)="$event.stopPropagation()">
      <button type="button" class="expert-modal__close" (click)="closeExpertModal()" aria-label="Close">
        <mat-icon>close</mat-icon>
      </button>
      <div class="expert-modal__avatar" [style.--expert-accent]="expertModal.accent">
        <mat-icon>{{ expertModal.icon }}</mat-icon>
      </div>
      <h3 class="expert-modal__role" [style.color]="expertModal.accent">{{ expertModal.role }}</h3>
      <p class="expert-modal__text">{{ expertModal.fullComment }}</p>
    </article>
  </div>

</div>
